import React, { useState } from 'react';
import styles from './scene.module.scss';
import cn from 'clsx';

export interface Scene {
  id: number;
  scene: string;
  sceneNumber: number;
  chapter: number;
  setting?: string;
  sceneDetails?: string;
  sceneReasoning?: string;
  needsEditing?: boolean;
}

interface SceneProps {
  scene: Scene;
  onSceneChange: (updatedScene: Scene) => void;
  handleDeleteScene: (scene: Scene) => void;
}

const SceneComponent = ({
  scene,
  onSceneChange,
  handleDeleteScene
}: SceneProps) => {
  const [sceneMap, setSceneMap] = useState<{ [key: number]: boolean }>({});

  const handleOpenScene = (sceneNumber: number) => {
    setSceneMap((prev) => ({
      ...prev,
      [sceneNumber]: !prev[sceneNumber],
    }));
  };

  const handleChange = (field: keyof Scene, value: string | boolean) => {
    const updatedScene = { ...scene, [field]: value };
    onSceneChange(updatedScene);
  };

  return (
    <div className={styles.sceneContainer}>
      <div>
        <div id={`scene-${scene.id}`} onClick={() => handleOpenScene(scene.sceneNumber)} className={styles.sceneHeader}>
          <div className={styles.sceneHeaderTitle}>
            <h2>Chapter {scene.chapter}, Scene {scene.sceneNumber}</h2>
            <div>
              <i className={cn(styles.arrow, { [styles.up]: sceneMap[scene.sceneNumber], [styles.down]: !sceneMap[scene.sceneNumber] })}></i>
            </div>
            {scene.needsEditing &&
              <div className={styles.warning}><span>!</span></div>
            }
          </div>
        </div>
        <textarea
          placeholder="Outline the main events happening in this scene..."
          value={scene.scene}
          onChange={(e) => handleChange('scene', e.target.value)}
        />
      </div>

      {sceneMap[scene.sceneNumber] && (
        <div className={styles.sceneDetailContainer}>
          <div>
            <h3>Setting:</h3>
            <textarea
              placeholder="Detail the time and place of this scene (e.g., a rainy day in Paris)..."
              value={scene.setting}
              onChange={(e) => handleChange('setting', e.target.value)}
            />
          </div>
          <div>
            <h3>Scene Details:</h3>
            <textarea
              placeholder="Add important details like dialogue, actions, and character emotions..."
              value={scene.sceneDetails}
              onChange={(e) => handleChange('sceneDetails', e.target.value)}
            />
          </div>
          <div>
            <h3>Scene Reasoning:</h3>
            <textarea
              placeholder="Explain the significance of this scene in your story's narrative..."
              value={scene.sceneReasoning}
              onChange={(e) => handleChange('sceneReasoning', e.target.value)}
            />
          </div>
          <div className={styles.sceneMeta}>
            <h3>Scene metadata:</h3>
            <div className={styles.numberInputContainer}>
              <label htmlFor="chapters">Chapter</label>
              <input
                type="number"
                id="chapters"
                onChange={(e) => handleChange('chapter', e.target.value)}
                defaultValue={scene.chapter}
                min="1"
                max="50"
              />
            </div>
            <div>
              <label htmlFor="needsEditing">Needs editing?</label>
              <input
                name="needsEditing"
                type="checkbox"
                onChange={(e) => handleChange('needsEditing', e.target.checked)}
                checked={scene.needsEditing}
              />
            </div>
            <div className={styles.deleteButtonContainer}>
              <button className={styles.negativeButton} onClick={() => handleDeleteScene(scene)}>Delete scene</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SceneComponent;
